import { useRef, useState } from 'react';
import { TbArrowDown, TbArrowUp, TbDownload, TbPlus, TbTrash } from 'react-icons/tb';
import { useAuth } from '../../auth/AuthContext';
import { errorMessageFor } from '../../auth/error-message';
import { ExerciseGif } from '../../training/ExerciseGif';
import { ExercisePicker } from '../../training/ExercisePicker';
import { downloadRoutine } from './routine-export';
import '../admin.css';

const NAME_MAX = 80;
const DAY_NAME_MAX = 60;
const REPS_MAX = 20;
const NOTE_MAX = 500;
const MIN_DAYS = 1;
const MAX_DAYS = 7;
const MIN_ITEMS = 1;
const MAX_ITEMS = 20;
const MIN_SETS = 1;
const MAX_SETS = 20;
const MIN_REST = 0;
const MAX_REST = 900;

function isIntegerString(value) {
  return /^-?\d+$/.test(String(value).trim());
}

// Mirrors api/src/gym/routine-service.js's own limits so the admin sees a
// mistake next to the field before ever reaching the server (the server
// keeps enforcing the same rules — this is a convenience, not the authority).
function validate(name, days) {
  const errors = {};
  const trimmedName = name.trim();
  if (!trimmedName) errors.name = 'El nombre es obligatorio.';
  else if (trimmedName.length > NAME_MAX) errors.name = `El nombre no puede superar ${NAME_MAX} caracteres.`;

  if (days.length < MIN_DAYS || days.length > MAX_DAYS) {
    errors.days = `La rutina debe tener entre ${MIN_DAYS} y ${MAX_DAYS} días.`;
  }

  days.forEach((day) => {
    const dayName = day.name.trim();
    if (!dayName) errors[`day-${day.key}-name`] = 'El nombre del día es obligatorio.';
    else if (dayName.length > DAY_NAME_MAX) errors[`day-${day.key}-name`] = `Máximo ${DAY_NAME_MAX} caracteres.`;

    if (day.items.length < MIN_ITEMS || day.items.length > MAX_ITEMS) {
      errors[`day-${day.key}-items`] = `Cada día debe tener entre ${MIN_ITEMS} y ${MAX_ITEMS} ejercicios.`;
    }

    day.items.forEach((item) => {
      if (!isIntegerString(item.sets) || Number(item.sets) < MIN_SETS || Number(item.sets) > MAX_SETS) {
        errors[`item-${item.key}-sets`] = `Entre ${MIN_SETS} y ${MAX_SETS}.`;
      }
      const reps = item.reps.trim();
      if (!reps) errors[`item-${item.key}-reps`] = 'Obligatorio.';
      else if (reps.length > REPS_MAX) errors[`item-${item.key}-reps`] = `Máximo ${REPS_MAX} caracteres.`;

      if (item.restSeconds !== '') {
        if (!isIntegerString(item.restSeconds) || Number(item.restSeconds) < MIN_REST || Number(item.restSeconds) > MAX_REST) {
          errors[`item-${item.key}-rest`] = `Entre ${MIN_REST} y ${MAX_REST} segundos.`;
        }
      }
      if (item.note.trim().length > NOTE_MAX) errors[`item-${item.key}-note`] = `Máximo ${NOTE_MAX} caracteres.`;
    });
  });

  return errors;
}

function buildBody(name, notes, days) {
  return {
    name: name.trim(),
    ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
    days: days.map((day) => ({
      ...(day.id ? { id: day.id } : {}),
      name: day.name.trim(),
      items: day.items.map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        exerciseId: item.exerciseId,
        sets: Number(item.sets),
        reps: item.reps.trim(),
        ...(item.restSeconds !== '' ? { restSeconds: Number(item.restSeconds) } : {}),
        ...(item.note.trim() !== '' ? { note: item.note.trim() } : {}),
      })),
    })),
  };
}

/**
 * Create/edit form for a member's routine (spec Task 7 "Nueva rutina"/"Editar"):
 * days (add/rename/remove/reorder), items per day (add via `ExercisePicker`,
 * edit sets/reps/rest/note, remove/reorder). Saving preserves the `id` of any
 * existing day/item so `routine-service.js#updateRoutine` updates it in place
 * (and keeps its substitution) instead of deleting + recreating it.
 *
 * When editing an item that currently has a student substitution, the
 * "prescribed" exercise shown/edited here is `originalExercise` (the coach's
 * own assignment) — `exercise` on the DTO is the student's effective
 * (substituted) one, which this editor never touches; the admin manages
 * substitutions from `RoutineSection`'s "Restaurar original", not here.
 */
export function RoutineEditor({
  api, memberId, routine, onCancel, onSaved,
}) {
  const { handleApiError } = useAuth();
  const nextKey = useRef(0);
  const newKey = () => `new-${nextKey.current += 1}`;

  function toEditableItem(item) {
    const exercise = item.originalExercise || item.exercise;
    return {
      key: item.id || newKey(),
      id: item.id,
      exerciseId: exercise.id,
      exercise,
      sets: String(item.sets),
      reps: item.reps,
      restSeconds: item.restSeconds != null ? String(item.restSeconds) : '',
      note: item.note || '',
    };
  }

  function toEditableDay(day) {
    return {
      key: day.id || newKey(), id: day.id, name: day.name, items: day.items.map(toEditableItem),
    };
  }

  function makeEmptyDay(position) {
    return {
      key: newKey(), id: undefined, name: `Día ${position}`, items: [],
    };
  }

  const [name, setName] = useState(routine ? routine.name : '');
  const [notes, setNotes] = useState(routine && routine.notes ? routine.notes : '');
  const [days, setDays] = useState(() => (
    routine ? routine.days.map(toEditableDay) : [makeEmptyDay(1)]
  ));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pickerDayIndex, setPickerDayIndex] = useState(null);
  const [pickerLimitNotice, setPickerLimitNotice] = useState('');
  const [pickerLimitDayIndex, setPickerLimitDayIndex] = useState(null);

  function updateDay(index, updater) {
    setDays((current) => current.map((day, i) => (i === index ? updater(day) : day)));
  }

  function addDay() {
    setDays((current) => (current.length >= MAX_DAYS ? current : [...current, makeEmptyDay(current.length + 1)]));
  }

  function removeDay(index) {
    setDays((current) => (current.length <= MIN_DAYS ? current : current.filter((_, i) => i !== index)));
  }

  function moveDay(index, direction) {
    setDays((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function renameDay(index, value) {
    updateDay(index, (day) => ({ ...day, name: value }));
  }

  function addItem(dayIndex, exercise) {
    updateDay(dayIndex, (day) => ({
      ...day,
      items: [...day.items, {
        key: newKey(), id: undefined, exerciseId: exercise.id, exercise, sets: '3', reps: '10', restSeconds: '60', note: '',
      }],
    }));
  }

  function removeItem(dayIndex, itemIndex) {
    updateDay(dayIndex, (day) => ({ ...day, items: day.items.filter((_, i) => i !== itemIndex) }));
  }

  function moveItem(dayIndex, itemIndex, direction) {
    updateDay(dayIndex, (day) => {
      const target = itemIndex + direction;
      if (target < 0 || target >= day.items.length) return day;
      const items = [...day.items];
      [items[itemIndex], items[target]] = [items[target], items[itemIndex]];
      return { ...day, items };
    });
  }

  function updateItemField(dayIndex, itemIndex, field, value) {
    updateDay(dayIndex, (day) => ({
      ...day,
      items: day.items.map((item, i) => (i === itemIndex ? { ...item, [field]: value } : item)),
    }));
  }

  function handleConfirmExercises(exercises) {
    const remaining = Math.max(0, MAX_ITEMS - (days[pickerDayIndex]?.items.length || 0));
    const accepted = exercises.slice(0, remaining);
    accepted.forEach((exercise) => addItem(pickerDayIndex, exercise));
    if (accepted.length < exercises.length) {
      const omitted = exercises.length - accepted.length;
      setPickerLimitNotice(`Se agregaron ${accepted.length} de ${exercises.length} ejercicios; ${omitted} no ${omitted === 1 ? 'entró' : 'entraron'} porque cada día admite hasta ${MAX_ITEMS}.`);
      setPickerLimitDayIndex(pickerDayIndex);
    }
    setPickerDayIndex(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    const validationErrors = validate(name, days);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitError('');
    setSubmitting(true);
    const body = buildBody(name, notes, days);
    try {
      const saved = routine ? await api.updateRoutine(routine.id, body) : await api.createRoutine(memberId, body);
      onSaved(saved);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      setSubmitError(errorMessageFor(caught));
      setSubmitting(false);
    }
  }

  return (
    <form className="gym-admin-routine-editor" onSubmit={handleSubmit} noValidate aria-label={routine ? 'Editar rutina' : 'Nueva rutina'}>
      <h3 className="gym-admin-routine-editor__title">{routine ? 'Editar rutina' : 'Nueva rutina'}</h3>
      {submitError && <p role="alert" className="gym-form__error">{submitError}</p>}

      <div className="gym-field">
        <label htmlFor="routine-name" className="gym-field__label">Nombre de la rutina</label>
        <input
          id="routine-name"
          type="text"
          className="gym-input"
          value={name}
          disabled={submitting}
          onChange={(event) => setName(event.target.value)}
          aria-describedby={errors.name ? 'routine-name-error' : undefined}
        />
        {errors.name && <p id="routine-name-error" role="alert" className="gym-admin-field-error">{errors.name}</p>}
      </div>

      <div className="gym-field">
        <label htmlFor="routine-notes" className="gym-field__label">Notas (opcional)</label>
        <textarea
          id="routine-notes"
          className="gym-input"
          value={notes}
          disabled={submitting}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>

      {errors.days && <p role="alert" className="gym-admin-field-error">{errors.days}</p>}

      <h4 className="gym-admin-routine-editor__section-title">Días</h4>
      <div className="gym-admin-routine-editor__days">
        {days.map((day, dayIndex) => (
          <div className="gym-admin-routine-editor__day" key={day.key} data-testid={`gym-admin-routine-editor-day-${dayIndex}`}>
            <div className="gym-admin-routine-editor__day-header">
              <label className="gym-admin-routine-editor__day-label" htmlFor={`day-name-${day.key}`}>Nombre del día</label>
              <input
                id={`day-name-${day.key}`}
                type="text"
                className="gym-input"
                value={day.name}
                disabled={submitting}
                aria-label={`Nombre del día ${dayIndex + 1}`}
                onChange={(event) => renameDay(dayIndex, event.target.value)}
              />
              <div className="gym-admin-routine-editor__day-actions">
                <button
                  type="button"
                  className="gym-button gym-button--secondary"
                  disabled={submitting || dayIndex === 0}
                  aria-label={`Subir día ${dayIndex + 1}`}
                  onClick={() => moveDay(dayIndex, -1)}
                >
                  <TbArrowUp aria-hidden="true" focusable="false" />Subir
                </button>
                <button
                  type="button"
                  className="gym-button gym-button--secondary"
                  disabled={submitting || dayIndex === days.length - 1}
                  aria-label={`Bajar día ${dayIndex + 1}`}
                  onClick={() => moveDay(dayIndex, 1)}
                >
                  <TbArrowDown aria-hidden="true" focusable="false" />Bajar
                </button>
                <button
                  type="button"
                  className="gym-button gym-button--secondary"
                  disabled={submitting || days.length <= MIN_DAYS}
                  aria-label={`Quitar día ${dayIndex + 1}`}
                  onClick={() => removeDay(dayIndex)}
                >
                  <TbTrash aria-hidden="true" focusable="false" />Quitar día
                </button>
              </div>
            </div>
            {errors[`day-${day.key}-name`] && <p role="alert" className="gym-admin-field-error">{errors[`day-${day.key}-name`]}</p>}
            {errors[`day-${day.key}-items`] && <p role="alert" className="gym-admin-field-error">{errors[`day-${day.key}-items`]}</p>}

            <h5 className="gym-admin-routine-editor__items-title">Ejercicios del día</h5>
            <ul className="gym-admin-routine-editor__items">
              {day.items.map((item, itemIndex) => (
                <li
                  className="gym-admin-routine-editor__item"
                  key={item.key}
                  data-testid={`gym-admin-routine-editor-item-${dayIndex}-${itemIndex}`}
                >
                  <div className="gym-admin-routine-editor__item-head">
                    <ExerciseGif exercise={item.exercise} size="sm" />
                    <span className="gym-admin-routine-editor__item-name">{item.exercise.name}</span>
                    <div className="gym-admin-routine-editor__item-actions">
                      <button
                        type="button"
                        className="gym-button gym-button--secondary"
                        disabled={submitting || itemIndex === 0}
                        aria-label={`Subir ejercicio — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        onClick={() => moveItem(dayIndex, itemIndex, -1)}
                      >
                        <TbArrowUp aria-hidden="true" focusable="false" />Subir
                      </button>
                      <button
                        type="button"
                        className="gym-button gym-button--secondary"
                        disabled={submitting || itemIndex === day.items.length - 1}
                        aria-label={`Bajar ejercicio — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        onClick={() => moveItem(dayIndex, itemIndex, 1)}
                      >
                        <TbArrowDown aria-hidden="true" focusable="false" />Bajar
                      </button>
                      <button
                        type="button"
                        className="gym-button gym-button--secondary"
                        disabled={submitting}
                        aria-label={`Quitar ${item.exercise.name} — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        onClick={() => removeItem(dayIndex, itemIndex)}
                      >
                        <TbTrash aria-hidden="true" focusable="false" />Quitar
                      </button>
                    </div>
                  </div>
                  <div className="gym-admin-routine-editor__item-fields">
                    <div className="gym-field">
                      <label className="gym-field__label" htmlFor={`item-sets-${item.key}`}>Series</label>
                      <input
                        id={`item-sets-${item.key}`}
                        className="gym-input"
                        inputMode="numeric"
                        value={item.sets}
                        disabled={submitting}
                        aria-label={`Series — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        aria-describedby={errors[`item-${item.key}-sets`] ? `item-sets-${item.key}-error` : undefined}
                        onChange={(event) => updateItemField(dayIndex, itemIndex, 'sets', event.target.value)}
                      />
                      {errors[`item-${item.key}-sets`] && (
                        <p id={`item-sets-${item.key}-error`} role="alert" className="gym-admin-field-error">{errors[`item-${item.key}-sets`]}</p>
                      )}
                    </div>
                    <div className="gym-field">
                      <label className="gym-field__label" htmlFor={`item-reps-${item.key}`}>Repeticiones</label>
                      <input
                        id={`item-reps-${item.key}`}
                        className="gym-input"
                        inputMode="numeric"
                        value={item.reps}
                        disabled={submitting}
                        aria-label={`Repeticiones — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        aria-describedby={errors[`item-${item.key}-reps`] ? `item-reps-${item.key}-error` : undefined}
                        onChange={(event) => updateItemField(dayIndex, itemIndex, 'reps', event.target.value)}
                      />
                      {errors[`item-${item.key}-reps`] && (
                        <p id={`item-reps-${item.key}-error`} role="alert" className="gym-admin-field-error">{errors[`item-${item.key}-reps`]}</p>
                      )}
                    </div>
                    <div className="gym-field">
                      <label className="gym-field__label" htmlFor={`item-rest-${item.key}`}>Descanso (segundos, opcional)</label>
                      <input
                        id={`item-rest-${item.key}`}
                        className="gym-input"
                        inputMode="numeric"
                        value={item.restSeconds}
                        disabled={submitting}
                        aria-label={`Descanso en segundos — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        aria-describedby={errors[`item-${item.key}-rest`] ? `item-rest-${item.key}-error` : undefined}
                        onChange={(event) => updateItemField(dayIndex, itemIndex, 'restSeconds', event.target.value)}
                      />
                      {errors[`item-${item.key}-rest`] && (
                        <p id={`item-rest-${item.key}-error`} role="alert" className="gym-admin-field-error">{errors[`item-${item.key}-rest`]}</p>
                      )}
                    </div>
                    <div className="gym-field">
                      <label className="gym-field__label" htmlFor={`item-note-${item.key}`}>Nota (opcional)</label>
                      <input
                        id={`item-note-${item.key}`}
                        className="gym-input"
                        value={item.note}
                        disabled={submitting}
                        aria-label={`Nota — día ${dayIndex + 1}, ejercicio ${itemIndex + 1}`}
                        aria-describedby={errors[`item-${item.key}-note`] ? `item-note-${item.key}-error` : undefined}
                        onChange={(event) => updateItemField(dayIndex, itemIndex, 'note', event.target.value)}
                      />
                      {errors[`item-${item.key}-note`] && (
                        <p id={`item-note-${item.key}-error`} role="alert" className="gym-admin-field-error">{errors[`item-${item.key}-note`]}</p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="gym-button gym-button--secondary"
              disabled={submitting}
              onClick={() => { setPickerLimitNotice(''); setPickerLimitDayIndex(null); setPickerDayIndex(dayIndex); }}
            >
              <TbPlus aria-hidden="true" focusable="false" />
              {`+ Agregar ejercicio — día ${dayIndex + 1}`}
            </button>
            {pickerLimitNotice && pickerLimitDayIndex === dayIndex && <p role="alert" className="gym-admin-field-error">{pickerLimitNotice}</p>}
          </div>
        ))}
      </div>

      <button type="button" className="gym-button gym-button--secondary" disabled={submitting || days.length >= MAX_DAYS} onClick={addDay}>
        <TbPlus aria-hidden="true" focusable="false" />+ Agregar día
      </button>

      <div className="gym-admin-form-actions">
        <button type="button" className="gym-button gym-button--secondary" disabled={submitting} onClick={onCancel}>
          Cancelar
        </button>
        {days.some((day) => day.items.length > 0) && (
          <button
            type="button"
            className="gym-button gym-button--secondary"
            disabled={submitting}
            onClick={() => downloadRoutine({ name, days })}
          >
            <TbDownload aria-hidden="true" focusable="false" />Descargar rutina
          </button>
        )}
        <button type="submit" disabled={submitting} className="gym-button gym-button--primary">
          {submitting ? 'Guardando…' : routine ? 'Guardar cambios' : 'Crear rutina'}
        </button>
      </div>

      {pickerDayIndex !== null && (
        <ExercisePicker api={api} mode="search" onConfirmSelection={handleConfirmExercises} onClose={() => setPickerDayIndex(null)} />
      )}
    </form>
  );
}
